// Centralized bookstore API client.
// Public catalog + auth + commerce calls. All requests are made with
// credentials so the `bookstore_session` HttpOnly cookie is carried.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const http = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const ANALYTICS_SESSION_KEY = 'cnr-storefront-analytics-session';
export function getStorefrontAnalyticsSession() {
  const now = Date.now();
  try {
    const saved = JSON.parse(localStorage.getItem(ANALYTICS_SESSION_KEY) || 'null');
    if (saved?.id && Number(saved.expires_at) > now) return saved.id;
    const id = `store_${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${now}_${Math.random().toString(36).slice(2)}`}`;
    localStorage.setItem(ANALYTICS_SESSION_KEY, JSON.stringify({ id, expires_at: now + 24 * 60 * 60 * 1000 }));
    return id;
  } catch {
    return `store_${now}_${Math.random().toString(36).slice(2)}`;
  }
}

export function trackStorefrontEvent(event) {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const eventId = `evt_${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
  return http.post('/bookstore/analytics/events', {
    event_id: eventId,
    session_id: getStorefrontAnalyticsSession(),
    page: window.location.pathname,
    ...event,
  }).then((response) => response.data).catch(() => null);
}

// Resolves a relative URL to a full URL the browser can load.
// Already-absolute URLs pass through unchanged.
export function resolveCoverUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  // Object-storage proxy: /api/bookstore-cover/{id} and /api/blog-cover/{id}
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Legacy local uploads prefix fallback
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return url;
}

export const bookstoreApi = {
  // public
  catalog:        ()       => http.get('/bookstore/catalog').then(r => r.data),
  communityDiversity: ()   => http.get('/bookstore/community-diversity').then(r => r.data),
  booksByGenre:   (slug)   => http.get(`/bookstore/genres/${slug}/books`).then(r => r.data),
  book:           (id)     => http.get(`/bookstore/books/${id}`).then(r => r.data),
  authorPage:     (slug)   => http.get(`/bookstore/authors/${slug}`).then(r => r.data),
  worldPage:      (slug)   => http.get(`/bookstore/worlds/${slug}`).then(r => r.data),
  // iter-p3-g — Language filter for genre pages.
  booksByGenreLanguage: (slug, language) =>
    http.get(`/bookstore/genres/${slug}/books`,
              { params: language ? { language } : undefined }).then(r => r.data),
  // iter-p3-g — Reader profile — update reading language.
  updatePreferences: (body) =>
    http.put('/bookstore/users/me/preferences', body).then(r => r.data),
  // waitlist
  joinWaitlist:   (email, source='bookstore_landing') =>
    http.post('/bookstore/waitlist', { email, source }).then(r => r.data),
  // customer auth
  register:       (body)   => http.post('/bookstore/auth/register', body).then(r => r.data),
  login:          (body)   => http.post('/bookstore/auth/login', body).then(r => r.data),
  logout:         ()       => http.post('/bookstore/auth/logout').then(r => r.data),
  // iter55g44 — Password recovery.
  requestPasswordReset:  (email)              => http.post('/bookstore/auth/password-reset/request', { email }).then(r => r.data),
  confirmPasswordReset:  (token, password)    => http.post('/bookstore/auth/password-reset/confirm', { token, new_password: password }).then(r => r.data),
  me:             ()       => http.get('/bookstore/auth/me').then(r => r.data),
  optionalMe:     ()       => http.get('/bookstore/auth/optional').then(r => r.data),
  // bookshelf + maven
  bookshelf:      ()       => http.get('/bookstore/bookshelf').then(r => r.data),
  mavenEligible:  ()       => http.get('/bookstore/maven/eligible').then(r => r.data),
  mavenClaim:     (book_id)=> http.post('/bookstore/maven/claim', { book_id }).then(r => r.data),
  // commerce
  checkoutBook:   (book_id, origin_url) =>
    http.post('/bookstore/checkout/book', { book_id, origin_url }).then(r => r.data),
  checkoutBundle: (bundle_id, origin_url) =>
    http.post('/bookstore/checkout/bundle', { bundle_id, origin_url }).then(r => r.data),
  subscribeMaven: (origin_url, preferred_genre=null) =>
    http.post('/bookstore/maven/subscribe', { origin_url, preferred_genre }).then(r => r.data),
  // iter-rh1 — Companion Reader handoff: best-effort, returns {ready:false,...}
  // when the Companion app isn't wired up yet, never throws on that.
  readerHandoff:  (session_id) =>
    http.get('/bookstore/checkout/reader-handoff', { params: { session_id } }).then(r => r.data),
  // iter-rh1 shelf variant — open an already-owned book from the shelf in
  // the Companion Reader without needing a Stripe session_id.
  shelfReaderOpen: (book_id) =>
    http.get(`/bookstore/shelf/${book_id}/reader-open`).then(r => r.data),
  // iter-50j — Phase D: Print-On-Demand (signed + unsigned softcover)
  podPricing:     (bookId) => http.get('/bookstore/pod/pricing', { params: bookId ? { book_id: bookId } : {} }).then(r => r.data),
  checkoutPod:    (body)   => http.post('/bookstore/checkout/pod', body).then(r => r.data),
  myPodOrders:    ()       => http.get('/bookstore/pod/my-orders').then(r => r.data),
  signatureAck:   (token)  => http.post('/pod/signature-ack', null, { params: { token } }).then(r => r.data),
  // iter-29 — bookstore language picker + translation
  languages:         () => http.get('/bookstore/languages').then(r => r.data),
  translationQuote:  (book_id, language) =>
    http.get(`/bookstore/books/${book_id}/quote`, { params: { language } }).then(r => r.data),
  translation:       (book_id, language) =>
    http.get(`/bookstore/books/${book_id}/translation`, { params: { language } }).then(r => r.data),
  // iter-romance28 — Public canonical content-tag list.
  canonicalTags:     ()       => http.get('/bookstore/canonical-tags').then(r => r.data),
  // iter-romance28 — Auth'd reader report card aggregation.
  reportCard:        ()       => http.get('/bookstore/report-card').then(r => r.data),
  // iter-p3-l — Reader wishlist.
  wishlist:         ()                       => http.get('/bookstore/wishlist').then(r => r.data),
  wishlistAdd:      (book_id, desired_language = null) =>
    http.post('/bookstore/wishlist', { book_id, desired_language }).then(r => r.data),
  wishlistRemove:   (book_id, desired_language = null) =>
    http.delete(`/bookstore/wishlist/${book_id}`,
                { params: desired_language ? { desired_language } : undefined }).then(r => r.data),
  // iter-p3-g — Reader profile — update reading language / heat / etc.
  updatePreferences: (body) =>
    http.put('/bookstore/users/me/preferences', body).then(r => r.data),
  // iter-romance26 — Public opt-in beacon for reader-warning telemetry.
  // Suppressed automatically when localStorage.bs-telemetry-opt-out = '1'.
  tagHiddenBeacon: ({ tag, slug = null, action = 'hide' }) => {
    try {
      if (localStorage.getItem('bs-telemetry-opt-out') === '1') return Promise.resolve({ ok: false, optedOut: true });
    } catch { /* SSR */ }
    return http.post('/bookstore/telemetry/tag-hidden', { tag, slug, action }).then(r => r.data).catch(() => null);
  },
  // Feb-2026 (indie-conv batch2) — Public trope master list for storefront filters.
  tropeMaster:      ()       => http.get('/bookstore/tropes/master').then(r => r.data),
  // Feb-2026 (indie-conv batch2 pt3) — Trending tropes rail.
  topTropes:        (limit = 12) =>
    http.get('/bookstore/tropes/top', { params: { limit } }).then(r => r.data),
  // Feb-2026 (indie-conv batch2 pt3) — Look-Inside reader telemetry beacon.
  lookInsideEvent:  (body) =>
    http.post('/bookstore/lookinside/events', body).then(r => r.data).catch(() => null),
  trackStorefrontEvent,
};

// ---------- Admin (re-uses the existing The Loom admin cookie) ----------
export const bookstoreAdminApi = {
  overview:        ()        => http.get('/admin/bookstore/overview').then(r => r.data),
  // catalog
  seed:            ()        => http.post('/admin/bookstore/seed').then(r => r.data),
  generateCovers:  (body)    => http.post('/admin/bookstore/generate-covers', body, { timeout: 600000 }).then(r => r.data),
  listBooks:       ()        => http.get('/admin/bookstore/books').then(r => r.data),
  createBook:      (body)    => http.post('/admin/bookstore/books', body).then(r => r.data),
  updateBook:      (id, p)   => http.put(`/admin/bookstore/books/${id}`, p).then(r => r.data),
  deleteBook:      (id)      => http.delete(`/admin/bookstore/books/${id}`).then(r => r.data),
  // iter-50f — reassign a bookstore_books row to a The Loom author so it
  // shows in their /stacks view (scoped by `source_project_id`).
  reassignBook:    (id, body) => http.put(`/admin/bookstore/books/${id}/reassign`, body).then(r => r.data),
  listGenres:      ()        => http.get('/admin/bookstore/genres').then(r => r.data),
  createGenre:     (body)    => http.post('/admin/bookstore/genres', body).then(r => r.data),
  updateGenre:     (id, p)   => http.put(`/admin/bookstore/genres/${id}`, p).then(r => r.data),
  deleteGenre:     (id)      => http.delete(`/admin/bookstore/genres/${id}`).then(r => r.data),
  // iter-53o — direct image upload for genre cards (multipart)
  uploadGenreImage: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/admin/bookstore/genres/${id}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  lockEmptyGenres: ()        => http.post('/admin/bookstore/genres/lock-empty').then(r => r.data),
  listBundles:     ()        => http.get('/admin/bookstore/bundles').then(r => r.data),
  createBundle:    (body)    => http.post('/admin/bookstore/bundles', body).then(r => r.data),
  updateBundle:    (id, p)   => http.put(`/admin/bookstore/bundles/${id}`, p).then(r => r.data),
  deleteBundle:    (id)      => http.delete(`/admin/bookstore/bundles/${id}`).then(r => r.data),
  // marketing
  getMarketing:    ()        => http.get('/admin/bookstore/marketing').then(r => r.data),
  saveMarketing:   (patch)   => http.put('/admin/bookstore/marketing', patch).then(r => r.data),
  // customers
  listUsers:       ()        => http.get('/admin/bookstore/users').then(r => r.data),
  listOrders:      ()        => http.get('/admin/bookstore/orders').then(r => r.data),
  listWaitlist:    ()        => http.get('/admin/bookstore/waitlist').then(r => r.data),
  // iter-40 — ebook GridFS uploader
  getBookFile:     (id)      => http.get(`/admin/bookstore/books/${id}/file`).then(r => r.data),
  uploadBookFile:  (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/admin/bookstore/books/${id}/file`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then(r => r.data);
  },
  deleteBookFile:  (id)      => http.delete(`/admin/bookstore/books/${id}/file`).then(r => r.data),
  // iter-53a — Admin Book Editor: cover image upload (with auto thumbnail).
  uploadBookCover: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/admin/bookstore/books/${id}/cover-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then(r => r.data);
  },
  // iter-53b — ISBN auto-fill via OpenLibrary.
  lookupIsbn:      (isbn)    => http.get('/admin/bookstore/isbn-lookup',
                                          { params: { isbn } }).then(r => r.data),
  // iter-53k — Single-book Nano-Banana cover regeneration (uses
  // back_copy / keywords / marketing_copy as steering).
  regenerateBookCover: (id) => http.post(`/admin/bookstore/books/${id}/regenerate-cover`,
                                          {}, { timeout: 120000 }).then(r => r.data),
  // iter-romance26 — AI-authored sample chapter (Emergent LLM · Claude 4.5).
  generateBookSample: (id) => http.post(`/admin/bookstore/books/${id}/generate-sample`,
                                          {}, { timeout: 120000 }).then(r => r.data),
  // iter-romance26 — Bulk Nano-Banana regen for every book on a shelf.
  regenerateShelfCovers: (slug, force = false) =>
    http.post(`/admin/bookstore/genres/${slug}/regenerate-covers`, null,
              { params: { force }, timeout: 900000 }).then(r => r.data),
  // iter-romance26 — Content-tag taxonomy + reader-warning telemetry.
  listContentTags:      ()   => http.get('/admin/bookstore/content-tags').then(r => r.data),
  tagHiddenTelemetry:   ()   => http.get('/admin/bookstore/telemetry/tag-hidden').then(r => r.data),
  // iter-romance28 — Bulk-refresh sample chapters for every book on a shelf.
  refreshShelfSamples:  (slug, force = false) =>
    http.post(`/admin/bookstore/genres/${slug}/refresh-samples`, null,
              { params: { force }, timeout: 900000 }).then(r => r.data),
  // iter-romance27 — Canonical content-tag taxonomy.
  getCanonicalTags:     ()      => http.get('/admin/bookstore/content-tags/canonical').then(r => r.data),
  setCanonicalTags:     (tags)  => http.put('/admin/bookstore/content-tags/canonical', { tags }).then(r => r.data),
  // Feb-2026 (indie-conv batch2) — Trope master list CRUD.
  getTropeMaster:       ()      => http.get('/admin/bookstore/tropes/master').then(r => r.data),
  setTropeMaster:       (tropes) => http.put('/admin/bookstore/tropes/master', { tropes }).then(r => r.data),
  // Feb-2026 (indie-conv batch2 pt3) — Look-Inside engagement analytics.
  lookInsideAnalytics:  () => http.get('/admin/bookstore/lookinside/analytics').then(r => r.data),
  // iter-50j — POD admin queue
  listPodOrders:   (status)  => http.get('/admin/pod/orders', { params: status ? { status } : {} }).then(r => r.data),
  updatePodStatus: (id, body) => http.put(`/admin/pod/orders/${id}/status`, body).then(r => r.data),
  // iter-50p — Marketing thumbnails admin CRUD
  listThumbnails:  ()        => http.get('/admin/bookstore/thumbnails').then(r => r.data),
  createThumbnail: (body)    => http.post('/admin/bookstore/thumbnails', body).then(r => r.data),
  updateThumbnail: (id, body) => http.put(`/admin/bookstore/thumbnails/${id}`, body).then(r => r.data),
  deleteThumbnail: (id)      => http.delete(`/admin/bookstore/thumbnails/${id}`).then(r => r.data),
  // Feb-2026 — Snapshot export/import for preview→production data sync
  downloadSnapshot: () => http.get('/admin/bookstore/snapshot',
                                    { responseType: 'blob', timeout: 60000 }).then(r => r.data),
  importSnapshot:   (snapshot, opts = {}) =>
    http.post('/admin/bookstore/snapshot',
              { snapshot, ...opts }, { timeout: 120000 }).then(r => r.data),
  // Feb-2026 — Authors CRUD + portrait management
  listAuthors:          ()       => http.get('/admin/bookstore/authors').then(r => r.data),
  updateAuthor:         (id, p)  => http.put(`/admin/bookstore/authors/${id}`, p).then(r => r.data),
  deleteAuthor:         (id)     => http.delete(`/admin/bookstore/authors/${id}`).then(r => r.data),
  regenerateAuthorPortrait: (id) => http.post(`/admin/bookstore/authors/${id}/regenerate-portrait`,
                                              {}, { timeout: 120000 }).then(r => r.data),
  regenerateAllPortraits: ()     => http.post('/admin/bookstore/generate-covers',
                                              { generate: 'authors', force: true },
                                              { timeout: 600000 }).then(r => r.data),
  // Feb-2026 — Force-regenerate all genre tiles via Nano Banana
  regenerateAllGenreTiles: ()    => http.post('/admin/bookstore/generate-covers',
                                              { generate: 'genres', force: true },
                                              { timeout: 600000 }).then(r => r.data),
  // New: AI draft bio for an author
  draftAuthorBio: (authorId) => http.post(`/admin/bookstore/authors/${authorId}/draft-bio`, {}).then(r => r.data),
  // New: Bulk author assignment
  bulkAssignAuthor: (body) => http.post('/admin/bookstore/books/bulk-assign-author', body).then(r => r.data),
  // New: Price history for a book
  priceHistory: (bookId) => http.get(`/admin/bookstore/price-history/${bookId}`).then(r => r.data),
  // New: Last snapshot sync time
  lastSyncTime: () => http.get('/admin/bookstore/last-sync-time').then(r => r.data),
  // New: Published inventory for financial panel
  publishedInventory: () => http.get('/admin/bookstore/published-inventory').then(r => r.data),
  // New: Live sales summary for financial panel
  salesSummary: (days = 30) => http.get(`/admin/bookstore/financials/sales-summary?days=${days}`).then(r => r.data),
  // New: Author royalties summary for Payouts tab
  authorRoyalties: () => http.get('/admin/bookstore/author-royalties').then(r => r.data),
  // New: Download payout PDF receipt
  payoutReceiptUrl: (payoutId) => `${http.defaults.baseURL}/admin/royalties/payout/${payoutId}/receipt`,
  uploadThumbnailImage: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return http.post(`/admin/bookstore/thumbnails/${id}/image`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};
